import React, { useEffect, useRef, useState } from 'react';
import { Plus, Wallet } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { AdminPage, ConfirmActionDialog, EmptyState } from '../../../components/design-system/admin';
import { usePlansApi } from '../../../common/hooks/use-plans-api.hook';
import { useAdminOverviewApi } from '../../../common/hooks/use-admin-overview-api.hook';
import { VendorPlan } from '../../../modules/superadmin/our-vendors/models/vendor.model';
import { PlanCard } from '../../../modules/superadmin/plans/PlanCard';
import { PlanComparisonTable } from '../../../modules/superadmin/plans/PlanComparisonTable';

/** Cards per row: one on phones, up to four on wide screens. */
const PLAN_GRID = 'grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4';

/**
 * The plans vendors can be on: a card per plan (drag to reorder — the order
 * vendors see), how many vendors each has, and a side-by-side comparison.
 */
export const PlansPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const plansApi = usePlansApi();
  const overviewApi = useAdminOverviewApi();
  const overviewApiRef = useRef(overviewApi);
  overviewApiRef.current = overviewApi;

  const [plans, setPlans] = useState<VendorPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; plan?: VendorPlan }>({ open: false });
  const [deleting, setDeleting] = useState(false);
  // Vendors per plan id, from the admin overview. `undefined` while it loads,
  // `null` when it failed — the cards then simply show no count.
  const [vendorCounts, setVendorCounts] = useState<Record<string, number> | null | undefined>(undefined);

  const loadPlans = async () => {
    setLoading(true);
    try {
      const data = (await plansApi.getPlans()) as VendorPlan[] | undefined;
      setPlans((data || []).slice().sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)));
    } catch {
      // ignore
    }
    setLoading(false);
  };

  useEffect(() => {
    loadPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const overview = await overviewApiRef.current.getOverview(30);
        if (!alive) return;
        if (!Array.isArray(overview?.plans)) {
          setVendorCounts(null);
          return;
        }
        const counts: Record<string, number> = {};
        overview.plans.forEach((p) => {
          if (p.id) counts[p.id] = p.vendors;
        });
        setVendorCounts(counts);
      } catch {
        if (alive) setVendorCounts(null);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const handleDelete = async (plan: VendorPlan) => {
    setDeleting(true);
    try {
      await plansApi.deletePlan(plan._id);
      setDeleteDialog({ open: false });
      loadPlans();
    } catch {
      toast.error(t('ADMIN_PLANS.LIST.DELETE_ERROR'));
    } finally {
      // Always release the dialog, or the next delete opens disabled.
      setDeleting(false);
    }
  };

  // Drag-and-drop reordering. A small activation distance keeps card buttons
  // clickable; the new order is persisted (sortOrder follows the sequence).
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setPlans((prev) => {
      const oldIndex = prev.findIndex((p) => p._id === active.id);
      const newIndex = prev.findIndex((p) => p._id === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      const next = arrayMove(prev, oldIndex, newIndex).map((p, i) => ({ ...p, sortOrder: i }));
      plansApi
        .reorderPlans(next.map((p) => p._id))
        .catch(() => {
          toast.error(t('PLANS.ADMIN.REORDER_ERROR'));
          loadPlans();
        });
      return next;
    });
  };

  const vendorsOn = (plan: VendorPlan): number | null | undefined => {
    if (vendorCounts === undefined) return undefined;
    if (vendorCounts === null) return null;
    return plan._id in vendorCounts ? vendorCounts[plan._id] : null;
  };

  return (
    <AdminPage
      title={t('ADMIN_SHELL.NAV.PLANS')}
      description={t('ADMIN_PLANS.LIST.DESCRIPTION')}
      actions={
        <Button onClick={() => navigate('/plans/new')} data-testid="create-plan-button">
          <Plus />
          {t('PLANS.ADMIN.CREATE')}
        </Button>
      }
    >
      {loading ? (
        <div className={PLAN_GRID} data-testid="admin-plans-loading" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-[30rem] rounded-xl" />
          ))}
        </div>
      ) : plans.length === 0 ? (
        <Card className="py-0" data-testid="admin-plans-empty">
          <EmptyState
            icon={<Wallet />}
            title={t('ADMIN_PLANS.LIST.EMPTY_TITLE')}
            description={t('ADMIN_PLANS.LIST.EMPTY_BODY')}
            className="py-16"
          />
        </Card>
      ) : (
        <>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={plans.map((p) => p._id)} strategy={rectSortingStrategy}>
              <div className={PLAN_GRID}>
                {plans.map((plan) => (
                  <PlanCard
                    key={plan._id}
                    plan={plan}
                    vendors={vendorsOn(plan)}
                    onEdit={() => navigate(`/plans/${plan._id}/edit`)}
                    onDelete={() => setDeleteDialog({ open: true, plan })}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>

          <PlanComparisonTable plans={plans} />
        </>
      )}

      <ConfirmActionDialog
        open={deleteDialog.open}
        onOpenChange={(open) => !open && setDeleteDialog({ open: false })}
        title={t('PLANS.ADMIN.DELETE_TITLE')}
        description={t('PLANS.ADMIN.DELETE_CONFIRM', { name: deleteDialog.plan?.name })}
        confirmLabel={t('ADMIN_SHELL.COMMON.DELETE')}
        destructive
        busy={deleting}
        onConfirm={() => deleteDialog.plan && handleDelete(deleteDialog.plan)}
      />
    </AdminPage>
  );
};

export default PlansPage;
